import React from "react";
import { Field, TextField, RadioField, InfoPanel, InlineCheck } from "./fields";
import { FileUpload } from "./FileUpload";
import { MIAM_OPTIONS, MIAM_DETAIL_TRIGGERS, MAX_UPLOAD_HINT } from "../../lib/options";
import { Button } from "../ui/button";

// Shared MIAM question block used by the existing-case and guidance enquiry forms.
export const MiamBlock = ({ d, u, config, prefix = "miam" }) => {
  const maxMb = config?.max_file_size_mb || 10;
  const val = d.miam;
  const showDetail = MIAM_DETAIL_TRIGGERS.includes(val);
  const showNoInfo = val === "no" || val === "unsure";
  const showArranged = val === "arranged";

  return (
    <div className="space-y-4">
      <RadioField options={MIAM_OPTIONS} value={val} onChange={(v) => u("miam", v)} name={prefix} testid={`${prefix}-options`} />

      {showDetail && (
        <div className="space-y-4 border-l-2 border-brand-gold pl-4">
          <Field label="Date of MIAM">
            <TextField type="date" value={d.miam_date} onChange={(v) => u("miam_date", v)} disabled={d.miam_date_unknown} testid={`${prefix}-date`} />
            <InlineCheck checked={d.miam_date_unknown} onChange={(v) => u("miam_date_unknown", !!v)} label="I do not know" testid={`${prefix}-date-unknown`} />
          </Field>
          <Field label="Did the mediator provide signed confirmation for a court application?" required>
            <RadioField options={["Yes", "No", "I do not know"]} value={d.miam_signed} onChange={(v) => u("miam_signed", v)} name={`${prefix}-signed`} testid={`${prefix}-signed`} />
          </Field>
          <Field label="Date of that confirmation">
            <TextField type="date" value={d.miam_conf_date} onChange={(v) => u("miam_conf_date", v)} disabled={d.miam_conf_date_unknown} testid={`${prefix}-conf-date`} />
            <InlineCheck checked={d.miam_conf_date_unknown} onChange={(v) => u("miam_conf_date_unknown", !!v)} label="I do not know" testid={`${prefix}-conf-date-unknown`} />
          </Field>
          <Field label="Upload the confirmation, if available" optional hint={MAX_UPLOAD_HINT(maxMb)}>
            <FileUpload value={d.miam_files} onChange={(v) => u("miam_files", v)} maxMb={maxMb} testid={`${prefix}-upload`} />
          </Field>
        </div>
      )}

      {showNoInfo && (
        <InfoPanel tone="gold" testid={`${prefix}-noinfo`}>
          <p>
            Before making certain family court applications, an applicant will usually need to attend a MIAM
            (Mediation Information Assessment Meeting), unless an exemption or another exception applies. The information you have provided may be
            relevant to an exemption, but further information or evidence may be required.
          </p>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={!!d.miam_referral} onChange={(e) => u("miam_referral", e.target.checked)} data-testid={`${prefix}-referral`} />
              Request a mediation referral
            </label>
            <a href="/miam" target="_blank" rel="noreferrer" className="text-xs text-brand-gold underline underline-offset-2" data-testid={`${prefix}-info-link`}>
              View current MIAM and exemption information
            </a>
          </div>
          {d.miam_referral && (
            <div className="mt-3 border-l-2 border-brand-gold bg-white/70 p-3 text-xs text-brand-darkgrey" data-testid={`${prefix}-referral-confirm`}>
              Thank you. We will pass your details to a mediation service so they can arrange a MIAM. A
              mediator will typically contact you within 2 business days using the contact information you
              provide on this form.
            </div>
          )}
        </InfoPanel>
      )}

      {showArranged && (
        <InfoPanel tone="grey" testid={`${prefix}-arranged`}>
          You may continue now or save this form and return after the MIAM. If mediation does not proceed or
          is not successful, you can update your answers at that stage.
        </InfoPanel>
      )}
    </div>
  );
};
